import { cloneDeep, merge } from 'lodash';
import { css } from 'styled-components';
import { themeGet } from 'styled-system';

const breakpoints = { xs: 0, sm: 480, md: 769, lg: 1024, xl: 1200, xxl: 1440 };

const zIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const space = [0, 5, 10, 15, 20];

const fonts = {
  base: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  roboto: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  robotoSlab: '"RobotoSlab", "Helvetica Neue", Helvetica, Arial, sans-serif'
};

const fontSizes = [14, 16, 18, 20, 28, 32, 38, 50, 64, 72];

const fontWeights = {
  light: 300,
  normal: 400,
  semiBold: 500,
  bold: 700
};

const colors = {
  textPrimary: '#18213c',
  textSecondary: '#4f5568',
  secondary: '#00aeeb',
  tertiary: '#203669',
  bgPrimary: '#f9f9f9',
  white: '#ffffff',
  text: '#4b4e55',
  gunMetal: '#4d535a',
  iron: '#f5f7fa',
  marine: '#00376b',
  azure: '#00a0ff',
  topaz: '#19abb4',
  cerulean: '#0277bd',
  aquaBlue: '#07c6ea',
  aquaSpring: '#ecfaf6',
  dodgerBlue: '#50abf1',
  clearBlueTwo: '#2b66ff',
  placeholder: '#727786',
  iceBlue: '#ebeff2',
  iceBlueTwo: '#ebf0f5',
  turquoise: '#02b5bd',
  greenBlue: '#22c993',
  hoverButton: '#2ce0a6',
  linkPrimary: '#00a0ff',
  linkPrimaryVisited: '#b40fb4',
  backgroundLight: '#f9fafb',
  weirdGreen: '#4acc8d',
  weirdGreenDarken: '#32b173',
  lightGrey: '#aab7c5',
  lightGreyBlueTwo: '#84bfda',
  superLightGrey: '#f7f7f7',
  monotoneGrey: '#555555',
  coolGrey: '#9ea9b2',
  lightBlue: '#e3effe',
  coolGreyTwo: '#a0a8b2',
  blueGrey: '#9ea2b9',
  blueGreyTwo: '#9ea1a9',
  paleGrey: '#f9fcff',
  paleGreyTwo: '#e4e4e4',
  paleGreyThree: '#fafbfd',
  darkGreyBlue: '#3a4c60',
  darkGreyBlueTwo: '#2f354f',
  whiteSmoke: '#ebebeb',
  ghostWhite: '#f6f8f8',
  cloudyBlue: '#c9cedb',
  cloudyBlueTwo: '#bfc1c6',
  paleSkyBlue: '#d8dfe5',
  paleSkyBlueTwo: '#d7dee4',
  turquoiseBlue: '#60d2e8',
  red: '#d60000',
  reddish: '#ff6f68',
  get error() {
    return this.reddish;
  },
  bgError: '#e74c3c1a',
  cookieBg: '#606066',
  linkedIn: '#0076b0',
  lightPurple: '#d6c0fd',
  boldPurple: '#4e3975',
  statusBadge: {
    grey: { bg: '#ebf0f5', color: '#4f5568' },
    yellow: { bg: '#ffecb3', color: '#ffa000' },
    green: { bg: '#c8e6c9', color: '#1b5e20' },
    blue: { bg: '#b3e5fc', color: '#01579b' },
    sainoo: { bg: '#e1bee7', color: '#4a148c' },
    darkGrey: { bg: '#737373', color: '#ffffff' },
    red: { bg: '#ffbbb6', color: '#ff6f68' },
    brown: { bg: '#917258', color: '#ffffff' }
  },
  loader: '#64b5f6',
  get icon() {
    return this.cloudyBlueTwo;
  },
  get iconHover() {
    return this.blueGreyTwo;
  },
  get disabled() {
    return this.cloudyBlueTwo;
  },
  get multiSelectItem() {
    return {
      bg: this.blueGreyTwo,
      color: this.white,
      hover: {
        bg: this.cloudyBlueTwo
      }
    };
  }
};

const borders = {
  none: 0,
  thin: '1px solid',
  normal: '2px solid',
  medium: '3px solid',
  paleGreyTwo: `1px solid ${colors.paleGreyTwo}`
};

const sizes = {
  xs: 28,
  sm: 34,
  md: 40,
  lg: 52,
  xl: 60,
  xxl: 100,
  input: 46,
  button: 52,
  appHeader: 60,
  footer: 70,
  legacySelect: 28,
  sidebar: {
    width: 54
  }
};

//remove after migration to v5
const heights = sizes;
const minHeights = {
  ...sizes,
  baseContent: '100vh',
  contentWithHeader: `calc(100vh - ${sizes.appHeader}px)`,
  contentWithFooter: `calc(100vh - ${sizes.appHeader}px - ${sizes.footer}px)`
};
const maxWidths = {
  formContentWidth: 590
};

const controlSizes = {
  xs: {
    padding: '0 8px',
    minHeight: `${sizes.xs}px`,
    fontSize: '12px'
  },
  sm: {
    padding: '0 15px',
    minHeight: `${sizes.sm}px`,
    fontSize: '14px'
  },
  md: {
    padding: '0 15px',
    minHeight: `${sizes.md}px`,
    fontSize: '14px'
  },
  lg: {
    padding: '0 15px',
    minHeight: `${sizes.lg}px`,
    fontSize: '16px'
  },
  baseTextarea: {
    padding: '10px 15px',
    resize: 'none'
  },
  textarea: {
    padding: '10px 15px',
    minHeight: '150px',
    resize: 'none'
  },
  textareaLight: {
    padding: '10px 15px',
    minHeight: '240px',
    fontWeight: fontWeights.light,
    lineHeight: '20px',
    resize: 'none'
  }
};

const buttonSizes = {
  xs: controlSizes.xs,
  sm: controlSizes.sm,
  md: {
    padding: '0 30px',
    height: `${sizes.md}px`,
    fontSize: '14px'
  },
  lg: controlSizes.lg
};

const textStyles = {
  normal: {
    textTransform: 'unset',
    textDecoration: 'none'
  },
  underline: {
    textDecoration: 'underline'
  },
  capitalize: {
    textTransform: 'capitalize'
  },
  firstLetter: {
    ':first-letter': {
      textTransform: 'capitalize'
    }
  },
  caps: {
    textTransform: 'uppercase'
  },
  ellipsis: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  nowrap: {
    whiteSpace: 'nowrap'
  }
};

const shadows = {
  shadow1: '0 1px 3px 0 rgba(77, 83, 90, 0.15)',
  shadow2: '0 2px 6px 0 rgba(77, 83, 90, 0.1)',
  card: '0 10px 17px 0 rgba(79, 85, 104, 0.15)',
  dropdownMenu: '0 2px 8px 0 rgba(79, 85, 104, 0.15)',
  image: '0 2px 40px 0 rgba(12, 16, 20, 0.2)'
};

const buttons = {
  bPrimary: css`
    padding: 0 15px;
    width: 100%;
    color: ${colors.white};
    background-color: ${colors.greenBlue};
    text-transform: uppercase;
    &:hover {
      background-color: ${colors.hoverButton};
    }
    &:disabled {
      color: ${colors.white};
      background-color: ${colors.iceBlue};
    }
  `,
  ca: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.weirdGreen}`,
    borderRadius: '2px',
    color: colors.weirdGreen,
    transition: 'background 0.2s cubic-bezier(0.4, 0, 1, 1)',
    '&:hover': {
      color: colors.white,
      background: colors.weirdGreen
    },
    '&:active': {
      color: colors.white,
      background: colors.weirdGreen
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      borderColor: colors.iceBlue,
      color: colors.white
    }
  },
  caInverted: {
    transition: 'background 0.2s cubic-bezier(0.4, 0, 1, 1)',
    padding: '0 20px',
    backgroundColor: colors.weirdGreen,
    border: `1px solid ${colors.weirdGreen}`,
    borderRadius: '2px',
    color: colors.white,
    '&:hover': {
      borderColor: colors.weirdGreenDarken,
      backgroundColor: colors.weirdGreenDarken
    },
    '&:active': {
      borderColor: colors.weirdGreenDarken,
      backgroundColor: colors.weirdGreenDarken
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      borderColor: colors.iceBlue,
      color: colors.white
    }
  },
  negative: {
    padding: '0 20px',
    backgroundColor: colors.error,
    borderRadius: '2px',
    color: colors.white,
    '&:hover': {
      backgroundColor: colors.red
    }
  },
  cancel: {
    backgroundColor: 'transparent',
    border: `1px solid ${colors.coolGreyTwo}`,
    borderRadius: '2px',
    color: colors.coolGreyTwo,
    '&:hover': {
      backgroundColor: colors.coolGreyTwo,
      color: colors.white
    }
  },
  linkedIn: css`
    background-color: ${colors.linkedIn};
    border: 1px solid ${colors.linkedIn};
    color: ${colors.white};
    svg {
      color: rgb(255, 255, 255, 0.6);
    }
    &:hover {
      background-color: ${colors.white};
      color: ${colors.linkedIn};
      svg {
        color: ${colors.linkedIn};
      }
    }
    &:disabled {
      background-color: ${colors.iceBlueTwo};
      border-color: ${colors.iceBlueTwo};
    }
  `,
  purple: css`
    color: ${colors.white};
    background-color: #ab87c7;
    border-radius: 2px;
    border: 1px solid #ab87c7;
    text-transform: capitalize;
    &:hover {
      background-color: ${colors.lightPurple};
    }
  `,
  // New UI
  secondary: css`
    border: 1px solid ${colors.textSecondary};
    border-radius: 2px;
    color: ${colors.textSecondary};
    &:not(:disabled):hover {
      background-color: #edeeef;
    }
    &:active {
      background-color: #dcdde1;
    }
    &:disabled {
      color: ${colors.cloudyBlueTwo};
      border: 1px solid ${colors.cloudyBlueTwo};
    }
  `,
  destroy: {
    borderRadius: 2,
    border: `1px solid ${colors.reddish}`,
    color: colors.reddish,
    '&:hover': {
      background: '#fff0ef'
    },
    '&:active': {
      background: '#ffe2e1'
    }
  }
};

const inputs = {
  primary: css`
    background-color: ${colors.white};
    border-color: ${colors.iceBlueTwo};
    border-radius: 4px;
    color: ${colors.textSecondary};
    &:hover {
      border-color: ${colors.cloudyBlueTwo};
    }
    &:focus-within {
      border-color: ${themeGet('colors.primary')};
    }
  `,
  get search() {
    return css`
      ${this.primary};
      padding-right: ${48 + 20}px;
      border-radius: 17px;
      outline: none;
      & + .icon {
        width: 48px;
        border-top-right-radius: 17px;
        border-bottom-right-radius: 17px;
        color: ${themeGet('colors.icon')};
      }
      &:focus {
        border: 1px solid ${themeGet('colors.primary')};
        & + .icon {
          background-color: ${themeGet('colors.header.searchbar')};
          color: ${themeGet('colors.primaryActive')};
        }
        & + .icon.open-menu {
          background-color: ${themeGet('colors.white')};
        }
        &.open {
          border-bottom: none;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          & + .icon + .menu {
            border-left: 1px solid ${themeGet('colors.primary')};
            border-right: 1px solid ${themeGet('colors.primary')};
            border-bottom: 1px solid ${themeGet('colors.primary')};
          }
          & + .icon {
            height: 100%;
            border-bottom-right-radius: 0;
          }
        }
      }
    `;
  },
  auth: css`
    padding: 0 15px;
    width: 100%;
    height: 46px !important;
    background-color: ${colors.white};
    border-color: ${colors.iceBlue};
    border-radius: 1px;
    color: ${colors.text};
    font-size: 16px !important;
    &::placeholder {
      color: ${colors.text};
    }
  `,
  secondary: {
    padding: '0px 15px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.paleSkyBlueTwo,
    backgroundColor: 'transparent',
    borderRadius: '4px',
    color: colors.textSecondary,
    fontSize: '14px',
    '&::placeholder': {
      color: '#a0a8b2'
    },
    '&:disabled': {
      color: '#cacfd4',
      '&::placeholder': {
        color: '#cacfd4'
      }
    },
    '&:focus': {
      '&::placeholder': {
        color: '#a0a8b2'
      }
    }
  },
  ca: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.iceBlue,
    borderRadius: '2px',
    color: colors.gunMetal,
    '&:hover': {
      borderColor: colors.cloudyBlueTwo
    },
    '&::placeholder': {
      color: colors.coolGrey
    }
  },
  ghost: {
    border: 'none',
    color: colors.textSecondary,
    height: '100%'
  },
  file: {
    opacity: 0,
    position: 'absolute',
    left: -10000
  }
};

const links = {
  underlineOnHover: {
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  auth: {
    color: colors.azure,
    '&:visited': colors.linkPrimaryVisited
  },
  primary: css`
    color: ${colors.textSecondary};
    &.active,
    &:hover {
      color: ${colors.textPrimary};
    }
  `,
  tertiary: css`
    position: relative;
    display: block;
    flex: 0 1 auto;
    font-size: 16px;
    font-weight: ${fontWeights.bold};
    line-height: 1;
    text-align: center;
    text-decoration: none;
    z-index: 10;
    color: ${colors.darkGreyBlue};
    color: ${props => props.state === 'disabled' && colors.coolGrey};
    color: ${props => props.state === 'completed' && colors.textSecondary};
    pointer-events: ${props => props.state === 'disabled' && 'none'};
    ${props => props.state === 'active' && tertiaryLinkActiveStyles};
    &:hover,
    &:focus {
      color: ${colors.aquaBlue};
      text-decoration: none;
    }
  `
};

const tertiaryLinkActiveStyles = css`
  && {
    color: ${colors.aquaBlue};
  }
  &:after {
    content: '';
    margin-top: 2px;
    display: block;
    width: 100%;
    height: 4px;
    background-color: ${colors.aquaBlue};
    white-space: nowrap;
  }
`;

const innerCircleDiameter = 8;
const outerCircleDiameter = 16;
const innerCircleRad = innerCircleDiameter / 2;
const outerCircleRad = outerCircleDiameter / 2;
const radiusDiff = outerCircleRad - innerCircleRad;

const radioButtons = {
  primary: css`
    & > input + div {
      position: relative;
      padding-left: ${outerCircleDiameter + 10}px;
    }
    & > input + div:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: ${outerCircleDiameter}px;
      height: ${outerCircleDiameter}px;
      border-width: 1px;
      border-style: solid;
      border-color: ${colors.cloudyBlueTwo};
      border-radius: 50%;
    }
    & > input + div:after {
      content: '';
      position: absolute;
      left: ${radiusDiff}px;
      top: 50%;
      width: ${innerCircleDiameter}px;
      height: ${innerCircleDiameter}px;
      background-color: ${colors.cloudyBlueTwo};
      border-radius: 50%;
      transition: all 0.2s ease;
    }
    & > input:not(:checked) + div:after {
      opacity: 0;
      transform: translateY(-50%) scale(0);
    }
    & > input:checked + div:after {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
    & > input:hover + div:before,
    & > input:focus + div:before,
    & > input:checked + div:before {
      border-color: ${themeGet('colors.primary')};
    }
    & > input:focus + div:after,
    & > input:checked + div:after {
      background-color: ${themeGet('colors.primary')};
    }
  `
};

const cards = {
  primary: {
    padding: '20px',
    backgroundColor: colors.white,
    borderRadius: '4px',
    boxShadow: shadows.shadow2
  }
};

const createColorStyle = (theme, initialColor, activeColor) => ({
  color: theme.colors[initialColor],
  '&:hover': {
    color: theme.colors[activeColor]
  }
});

const createButtonColorStyle = (theme, bg, color) => css`
  &,
  &:focus {
    background-color: ${theme.colors[bg]};
    border-color: currentColor;
    color: ${theme.colors[color]};
  }
  &:hover {
    background-color: ${theme.colors[color]};
    border-color: ${theme.colors[color]};
    color: ${theme.colors[bg]};
  }
`;
const createInvertedButtonColorStyle = (theme, bg, color) => css`
  &,
  &:focus {
    background-color: ${theme.colors[color]};
    border-color: ${theme.colors[color]};
    color: ${theme.colors[bg]};
  }
  &:hover {
    background-color: ${theme.colors[bg]};
    color: ${theme.colors[color]};
  }
`;

const getCommonColorStyles = theme => ({
  primary: createColorStyle(theme, 'primary', 'primaryHover'),
  secondary: createColorStyle(theme, 'secondary', 'secondaryHover'),
  textPrimary: createColorStyle(theme, 'textPrimary', 'primary'),
  secondaryToWhite: createColorStyle(theme, 'secondary', 'white'),
  tertiaryToSecondary: createColorStyle(theme, 'tertiary', 'secondary'),
  cloudyBlueTwoToBlueGreyTwo: createColorStyle(theme, 'cloudyBlueTwo', 'blueGreyTwo'),
  coolGreyTwoToSecondary: createColorStyle(theme, 'coolGreyTwo', 'secondary'),
  coolGreyTwoToGunMetal: createColorStyle(theme, 'coolGreyTwo', 'textSecondary'),
  cloudyBlueTwoToSecondary: createColorStyle(theme, 'cloudyBlueTwo', 'textSecondary'),
  icon: createColorStyle(theme, 'icon', 'iconHover'),
  iconToPrimary: createColorStyle(theme, 'icon', 'primary'),
  iconToSecondary: createColorStyle(theme, 'icon', 'secondary'),
  textSecondaryToPrimary: createColorStyle(theme, 'textSecondary', 'textPrimary'),
  gunMetalToDarkGreyBlueTwo: createColorStyle(theme, 'textSecondary', 'darkGreyBlueTwo'),
  paleGreyTwo: createColorStyle(theme, 'paleGreyTwo', 'aquaBlue'),
  darkGreyBlueToAquaBlue: createColorStyle(theme, 'darkGreyBlue', 'aquaBlue'),
  whiteToAquaBlue: createColorStyle(theme, 'white', 'aquaBlue'),
  whiteSmokeToWhite: createColorStyle(theme, 'whiteSmoke', 'white'),
  coolGreyToAquaBlue: createColorStyle(theme, 'coolGrey', 'aquaBlue'),
  weirdGreen: createColorStyle(theme, 'weirdGreen', 'weirdGreenDarken'),
  textSecondary: createButtonColorStyle(theme, 'white', 'textSecondary'),
  secondaryInverted: createInvertedButtonColorStyle(theme, 'white', 'secondary'),
  disabled: css`
    color: ${colors.white};
    background-color: ${colors.iceBlue};
  `
});

const setColorStyles = theme => {
  theme.colorStyles = {
    ...getCommonColorStyles(theme),
    ...theme.colorStyles
  };
  return theme;
};

export const basicTheme = {
  breakpoints,
  zIndices,
  getCommonColorStyles,
  space,
  heights,
  minHeights,
  maxWidths,
  sizes,
  buttonSizes,
  controlSizes,
  fonts,
  fontSizes,
  fontWeights,
  colors,
  borders,
  textStyles,
  shadows,
  buttons,
  inputs,
  radioButtons,
  links,
  cards
};

export const createTheme = <T>(overridingTheme: T) => {
  const newTheme = merge(cloneDeep(basicTheme), overridingTheme);
  setColorStyles(newTheme);
  return newTheme;
};
